import { BarChart, Bar, XAxis, YAxis, Cell, ResponsiveContainer, Tooltip, ReferenceLine } from "recharts";
import { Reveal } from "../components/Reveal";
import { SectionHead } from "../components/SectionHead";

const parseDeflection = (s) => {
  const m = String(s).match(/(\d+([.,]\d+)?)/);
  return m ? parseFloat(m[1].replace(",", ".")) : 0;
};

export const Verification = ({ t, chart }) => {
  const chartData = t.table_rows.map((row) => ({ name: row[0], d: parseDeflection(row[3]) }));
  return (
  <section id="verification" data-testid="section-verification" className="py-32 md:py-48 border-b border-[#272A2E]">
    <div className="max-w-[1600px] mx-auto px-6 md:px-10">
      <Reveal><SectionHead index={t.index} title={t.title} lead={t.lead} /></Reveal>

      <Reveal delay={0.1}>
        <div data-testid="wind-table" className="overflow-x-auto border border-[#272A2E] bg-[#0A0B0D]">
          <table className="w-full text-left font-mono text-sm">
            <thead>
              <tr className="border-b border-[#272A2E]">
                {t.table_head.map((h) => (
                  <th key={h} className="px-6 py-4 text-[10px] tracking-[0.2em] uppercase text-[#FF2A00] font-normal">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {t.table_rows.map((row, i) => (
                <tr key={i} data-testid={`wind-row-${i}`} className="border-b border-[#272A2E] last:border-b-0 hover:bg-[#121417] transition-colors duration-300">
                  {row.map((cell, j) => (
                    <td key={j} className={`px-6 py-5 tracking-tight whitespace-nowrap ${j === 0 ? "text-white" : j === 4 ? "text-[#8A8F98] text-xs" : "text-[#C8CCD2]"}`}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Reveal>

      <Reveal delay={0.12}>
        <div data-testid="deflection-chart" className="mt-6 md:mt-10 border border-[#272A2E] bg-[#0A0B0D] p-6 md:p-10">
          <p className="text-xs font-mono tracking-[0.2em] uppercase text-[#FF2A00] mb-8">// {chart.title}</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 16, right: 8, left: 0, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fill: "#8A8F98", fontSize: 11, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#272A2E" }} stroke="#272A2E" />
                <YAxis tick={{ fill: "#8A8F98", fontSize: 11, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={false} unit=" cm" stroke="#272A2E" />
                <Tooltip cursor={{ fill: "#121417" }} contentStyle={{ background: "#0A0B0D", border: "1px solid #272A2E", fontFamily: "JetBrains Mono", fontSize: 12 }} labelStyle={{ color: "#fff" }} itemStyle={{ color: "#FF2A00" }} />
                <ReferenceLine y={5} stroke="#FF2A00" strokeDasharray="4 4" label={{ value: chart.limit, fill: "#FF2A00", fontSize: 10, fontFamily: "JetBrains Mono", position: "insideTopLeft" }} />
                <Bar dataKey="d" name={chart.unit} isAnimationActive animationDuration={1400}>
                  {chartData.map((e, i) => (
                    <Cell key={i} fill={e.d <= 5 ? "#8A8F98" : "#FF2A00"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Reveal>

      <div className="mt-16 grid md:grid-cols-2 gap-6 md:gap-10">
        {[
          { title: t.strengths_t, items: t.strengths, accent: true, id: "strengths" },
          { title: t.weak_t, items: t.weak, accent: false, id: "weaknesses" },
        ].map((col) => (
          <Reveal key={col.id} delay={0.15}>
            <div data-testid={`verification-${col.id}`} className="border border-[#272A2E] bg-[#0A0B0D] p-8 md:p-10 h-full" style={{ clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 24px), calc(100% - 24px) 100%, 0 100%)" }}>
              <h3 className={`font-display text-xl font-bold uppercase tracking-tight ${col.accent ? "text-white" : "text-[#FF2A00]"}`}>{col.title}</h3>
              <ul className="mt-8 space-y-5">
                {col.items.map((item, i) => (
                  <li key={i} className="flex gap-4 text-sm font-mono tracking-tight text-[#8A8F98] leading-relaxed">
                    <span className={`shrink-0 ${col.accent ? "text-[#FF2A00]" : "text-[#8A8F98]"}`}>{col.accent ? "+" : "!"}</span>{item}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2}>
        <div data-testid="verification-verdict" className="mt-6 md:mt-10 border-l-2 border-[#FF2A00] bg-[#0A0B0D] p-8 md:p-10">
          <p className="text-xs font-mono tracking-[0.2em] uppercase text-[#FF2A00] mb-4">{t.verdict_t}</p>
          <p className="text-sm md:text-base font-mono tracking-tight text-[#C8CCD2] leading-relaxed max-w-4xl">{t.verdict}</p>
        </div>
      </Reveal>
    </div>
  </section>
  );
};
